/**
 * Pure helpers for the sheet-review screen (spec §5.3): the Edit-table draft
 * reducer plus the counts, messages and orderings the review components draw.
 * Kept out of the components so every rule is unit-tested without rendering.
 */
import type { ExpectedField, ImportBatchRow, RowChangeSet, RowChoice } from '@discovr/types';
import { decisionsForAll, emptyChangeSet, isEmptyChangeSet } from '@discovr/features';
import { importIdentityWording } from './importIdentityWording';
import { USERNAME_KEY, countLabel, isBlank, isFrozenOnLockedMatch, isStatField, rosterComparisonFields } from './importWizardHelpers';
import { isBlockingStatus, isDroppedRow, reviewRowStatus, rowChoice } from './reviewRowStatus';
import { sheetCellDiffers, sheetCellsEqual } from './sheetValueAgreement';
import type { ReviewTab, ReviewTabContents } from './reviewTabs';

// The review components take their count wording from here.
export { countLabel };

const PART_SEPARATOR = ', ';
const UNDECIDED_CHOICE: RowChoice = 'undecided';
const NO_CURRENT_VALUE_TEXT = 'not set';

export interface GridDraft {
  values: Record<number, Record<string, string>>;
  deletes: Set<number>;
}

export type EditShortcut = 'save' | 'cancel';

export interface ShortcutKey {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  /** Mid-IME-composition keys (Esc, Enter) belong to the input method. */
  isComposing?: boolean;
}

export interface CellVersions {
  kept: string;
  other: string;
  /** False while the row still waits for its Current/Sheet pick: striking a
   *  value through then would read as if the sheet had already won. */
  decided: boolean;
}

/** The banner's two lines: how many issues, then what they are. */
export interface ReviewIssuesMessage {
  title: string;
  detail: string;
}

export interface ExistingCreatorsSummary {
  total: number;
  undecided: number;
  willUpdate: number;
  keepingCurrent: number;
}

/** Shared by the grid and the Duplicates tab so both scroll inside the same
 *  height and the wizard footer stays in view. */
export const REVIEW_SCROLL_MAX_HEIGHT_CLASS = 'max-h-[60vh]';

export function startDraft(rows: ImportBatchRow[], fields: string[]): GridDraft {
  const values: GridDraft['values'] = {};
  for (const row of rows) {
    values[row.rowNumber] = Object.fromEntries(fields.map((key) => [key, row.values[key] ?? '']));
  }
  return { values, deletes: new Set() };
}

export function setDraftCell(d: GridDraft, rowNumber: number, key: string, value: string): GridDraft {
  const rowValues = { ...d.values[rowNumber], [key]: value };
  return { values: { ...d.values, [rowNumber]: rowValues }, deletes: d.deletes };
}

export function toggleDraftDelete(d: GridDraft, rowNumber: number): GridDraft {
  const deletes = new Set(d.deletes);
  if (deletes.has(rowNumber)) deletes.delete(rowNumber);
  else deletes.add(rowNumber);
  return { values: d.values, deletes };
}

function changedCells(draftValues: Record<string, string>, row: ImportBatchRow): Record<string, string> {
  return Object.fromEntries(Object.entries(draftValues).filter(([key, value]) => value !== (row.values[key] ?? '')));
}

export function draftToChangeSet(d: GridDraft, rows: ImportBatchRow[]): RowChangeSet {
  const changes = emptyChangeSet();
  for (const row of rows) {
    const draftValues = d.values[row.rowNumber];
    if (!draftValues || d.deletes.has(row.rowNumber)) continue;
    const values = changedCells(draftValues, row);
    if (Object.keys(values).length > 0) changes.edits.push({ rowNumber: row.rowNumber, values });
  }
  changes.deletes = [...d.deletes].sort((a, b) => a - b);
  return changes;
}

/** Whether Save would send anything: what Cancel would throw away. */
export function draftHasChanges(d: GridDraft, rows: ImportBatchRow[]): boolean {
  return !isEmptyChangeSet(draftToChangeSet(d, rows));
}

export function draftChangeMessage(cs: RowChangeSet): string {
  const saved = 'Changes saved';
  if (cs.deletes.length === 0) return saved;
  return `${saved} · ${countLabel(cs.deletes.length, 'row', 'rows')} deleted`;
}

/** Mapped fields in expectedFields order, username always first: a creator
 *  can't be found without it, so the grid shows it even before it's mapped. */
export function gridFields(expectedFields: ExpectedField[], mapping: Record<string, string>): ExpectedField[] {
  const mapped = new Set(Object.values(mapping));
  const username = expectedFields.filter((f) => f.key === USERNAME_KEY);
  const others = expectedFields.filter((f) => f.key !== USERNAME_KEY && mapped.has(f.key));
  return [...username, ...others];
}

export function editShortcut(event: ShortcutKey): EditShortcut | null {
  if (event.isComposing) return null;
  if (event.key === 'Escape') return 'cancel';
  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) return 'save';
  return null;
}

export function hasRosterMatches(rows: ImportBatchRow[]): boolean {
  return rows.some((row) => row.rosterMatch !== null);
}

export function countBlocking(rows: ImportBatchRow[]): number {
  return rows.filter((row) => !isDroppedRow(row) && isBlockingStatus(reviewRowStatus(row))).length;
}

function duplicateRows(contents: ReviewTabContents): ImportBatchRow[] {
  return contents.duplicateGroups.flatMap((group) => group.rows);
}

export function tabRows(contents: ReviewTabContents, tab: ReviewTab): ImportBatchRow[] {
  if (tab === 'all') return contents.all;
  if (tab === 'needs-fixing') return contents.needsFixing;
  if (tab === 'duplicates') return duplicateRows(contents);
  return contents.ready;
}

export type RowOrderSnapshot = Record<ReviewTab, number[]>;

function rowNumbersOf(rows: ImportBatchRow[]): number[] {
  return rows.map((row) => row.rowNumber);
}

export function tabRowOrder(contents: ReviewTabContents): RowOrderSnapshot {
  return {
    all: rowNumbersOf(contents.all),
    'needs-fixing': rowNumbersOf(contents.needsFixing),
    duplicates: rowNumbersOf(duplicateRows(contents)),
    ready: rowNumbersOf(contents.ready),
  };
}

export function rowsInOrder(order: number[], rows: ImportBatchRow[]): ImportBatchRow[] {
  const byNumber = new Map(rows.map((row) => [row.rowNumber, row]));
  return order.flatMap((rowNumber) => byNumber.get(rowNumber) ?? []);
}

function errorKeys(row: ImportBatchRow): string[] {
  return Object.keys(row.validationErrors);
}

function isMissingUsername(row: ImportBatchRow, key: string): boolean {
  return key === USERNAME_KEY && isBlank(row.values[USERNAME_KEY]);
}

function isUndecidedExisting(row: ImportBatchRow): boolean {
  return reviewRowStatus(row) === 'in-campaign';
}

interface IssuePart {
  count: number;
  singular: string;
  plural: string;
}

function issuePartLabel(part: IssuePart): string {
  return countLabel(part.count, part.singular, part.plural);
}

export function reviewIssuesMessage(contents: ReviewTabContents, campaignPlatform: string): ReviewIssuesMessage | null {
  const identity = importIdentityWording(campaignPlatform);
  const errors = contents.all.flatMap((row) => errorKeys(row).map((key) => isMissingUsername(row, key)));
  const missing = errors.filter(Boolean).length;
  const parts: IssuePart[] = [
    { count: missing, singular: identity.missingSingular, plural: identity.missingPlural },
    { count: errors.length - missing, singular: 'invalid value', plural: 'invalid values' },
    { count: contents.duplicateGroups.length, singular: 'duplicate creator', plural: 'duplicate creators' },
    { count: contents.all.filter(isUndecidedExisting).length, singular: 'already in this campaign', plural: 'already in this campaign' },
  ];
  const total = parts.reduce((sum, part) => sum + part.count, 0);
  if (total === 0) return null;
  const listed = parts.filter((part) => part.count > 0).map(issuePartLabel);
  return {
    title: `${countLabel(total, 'issue', 'issues')} to fix before import`,
    detail: `${listed.join(PART_SEPARATOR)}.`,
  };
}

/** The grid's name for a column. The server's label for the identity column
 *  is the long dropdown one ("Username / Profile URL"); the grid uses the
 *  platform's short word for it. */
export function reviewColumnLabel(field: ExpectedField, campaignPlatform: string): string {
  if (field.key === USERNAME_KEY) return importIdentityWording(campaignPlatform).columnLabel;
  return field.label;
}

/** The kept and other values of a matched row's cell, or null when there's
 *  nothing to compare (new creator, locked price, or a sheet cell that reads as
 *  the same value or that the import ignores). An undecided row shows its
 *  sheet value as kept but is marked not decided. */
export function cellVersions(row: ImportBatchRow, field: ExpectedField, payoutModel?: string): CellVersions | null {
  const match = row.rosterMatch;
  if (match === null) return null;
  const comparable = rosterComparisonFields([field], match.locked, row.values, match.statsFrozen === true);
  if (comparable.length === 0) return null;
  const sheet = row.values[field.key] ?? '';
  const current = match.currentValues[field.key] ?? '';
  if (!sheetCellDiffers(match, field, sheet, payoutModel)) return null;
  const choice = rowChoice(row);
  if (choice === 'current') return { kept: current, other: sheet, decided: true };
  return { kept: sheet, other: current, decided: choice !== UNDECIDED_CHOICE };
}

/** What an undecided cell says about the value the campaign holds today. */
export function currentValueNote(current: string): string {
  return `Current: ${isBlank(current) ? NO_CURRENT_VALUE_TEXT : current}`;
}

function isFrozenStatCell(row: ImportBatchRow, field: ExpectedField): boolean {
  return row.rosterMatch?.statsFrozen === true && isStatField(field);
}

/** A cell commit ignores on a matched creator — a locked match's prices and
 *  usage-rights days, or a frozen creator's stats — shown (and, in edit mode,
 *  kept) read-only at the current value. */
export function isLockedCell(row: ImportBatchRow, field: ExpectedField): boolean {
  if (isFrozenStatCell(row, field)) return true;
  return row.rosterMatch?.locked === true && isFrozenOnLockedMatch(field);
}

const STATS_FROZEN_TITLE = 'Stats are frozen for this creator, so the sheet\'s value is not used';

/** The hover text of a read-only cell: why the sheet's value is not used. */
export function lockedCellTitle(row: ImportBatchRow, field: ExpectedField): string {
  if (isFrozenStatCell(row, field)) return STATS_FROZEN_TITLE;
  return lockedPriceTitle(row.rosterMatch?.status);
}

// Go rosterDomain.StatusOutreach: the commit rejects such a row and writes
// nothing, so "locked" alone would wrongly suggest its stats still update.
const OUTREACH_ROSTER_STATUS = 'outreach';
const OUTREACH_LOCKED_TITLE = 'In outreach, so this row will be skipped';

export function lockedPriceTitle(status: string | undefined): string {
  if (status === OUTREACH_ROSTER_STATUS) return OUTREACH_LOCKED_TITLE;
  const readable = (status ?? '').replaceAll('_', ' ').trim();
  if (readable === '') return 'Prices are locked';
  return `Prices are locked: this creator is ${readable}`;
}

// "Decidable" is whatever the bulk actions would send a decision for, so the
// bar's counts can never disagree with what its buttons save.
export function existingCreatorsSummary(rows: ImportBatchRow[]): ExistingCreatorsSummary {
  const decidable = new Set(decisionsForAll(rows, 'undecided').map((decision) => decision.rowNumber));
  const choices = rows.filter((row) => decidable.has(row.rowNumber)).map(rowChoice);
  return {
    total: decidable.size,
    undecided: choices.filter((choice) => choice === 'undecided').length,
    willUpdate: choices.filter((choice) => choice === 'sheet').length,
    keepingCurrent: choices.filter((choice) => choice === 'current').length,
  };
}

/** The columns where a duplicate group's rows disagree. Values the server
 *  reads as equal ("12k" and 12000) are not a disagreement. */
export function differingFieldKeys(rows: ImportBatchRow[], fields: ExpectedField[]): Set<string> {
  const first = rows[0];
  if (first === undefined) return new Set();
  const differs = (field: ExpectedField) =>
    rows.some((row) => !sheetCellsEqual(field, first.values[field.key] ?? '', row.values[field.key] ?? ''));
  return new Set(fields.filter(differs).map((field) => field.key));
}

export function keepRowMessage(kept: number, removed: number[]): string {
  const noun = removed.length === 1 ? 'row' : 'rows';
  return `Kept row ${kept} · removed ${noun} ${removed.join(PART_SEPARATOR)}`;
}

export function deletesChangeSet(rowNumbers: number[]): RowChangeSet {
  return { ...emptyChangeSet(), deletes: rowNumbers };
}

export function restoresChangeSet(rowNumbers: number[]): RowChangeSet {
  return { ...emptyChangeSet(), restores: rowNumbers };
}

export function decisionsChangeSet(decisions: RowChangeSet['decisions']): RowChangeSet {
  return { ...emptyChangeSet(), decisions };
}

/** A row that just left the tab, remembered with where it sat so it can fade
 *  out in place instead of vanishing. */
export interface DepartedRow {
  row: ImportBatchRow;
  index: number;
}

export function departedRows(previous: ImportBatchRow[], current: ImportBatchRow[]): DepartedRow[] {
  const stillShown = new Set(rowNumbersOf(current));
  return previous.flatMap((row, index) => (stillShown.has(row.rowNumber) ? [] : [{ row, index }]));
}

export function withDepartedRows(current: ImportBatchRow[], departed: DepartedRow[]): ImportBatchRow[] {
  if (departed.length === 0) return current;
  const merged = [...current];
  for (const { row, index } of departed) merged.splice(Math.min(index, merged.length), 0, row);
  return merged;
}

export interface ImportGate {
  busy: boolean;
  editing: boolean;
  blocking: number;
  importable: number;
}

export function isImportDisabled(gate: ImportGate): boolean {
  return gate.busy || gate.editing || gate.blocking > 0 || gate.importable === 0;
}

/** Skipping only makes sense when something would still be imported. */
export function isSkipDisabled(gate: ImportGate): boolean {
  return gate.busy || gate.editing || gate.importable === 0;
}
